import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Copy, Check, Gift, Users, Download, Link2, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navigation from "../components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || window.location.origin;

const ReferralPage = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/referral/my-stats`, { headers })
      .then(r => setStats(r.data))
      .catch(err => console.error(err));
  }, [user]);

  const referralLink = stats ? `${BASE_URL}/login?ref=${stats.referral_code}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({ title: "Join The Archive", text: "Check out The Archive!", url: referralLink });
    } else {
      copyLink();
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Gift className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Sign in to access your referral dashboard</p>
          <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => navigate("/login")}>Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }} data-testid="referral-title">
            Refer & Earn
          </h1>
          <p className="text-slate-500 mt-1">Invite friends and earn free download credits</p>
        </div>

        {stats && (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center" data-testid="stat-credits">
                <Download className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-900">{stats.referral_credits}</p>
                <p className="text-sm text-slate-500">Download Credits</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center" data-testid="stat-referrals">
                <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-900">{stats.referral_count}</p>
                <p className="text-sm text-slate-500">Friends Referred</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center" data-testid="stat-per-referral">
                <Gift className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-900">{stats.credits_per_referral}</p>
                <p className="text-sm text-slate-500">Credits Per Referral</p>
              </div>
            </div>

            {/* Referral link */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="referral-link-section">
              <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Fraunces, serif" }}>
                <Link2 className="w-5 h-5 inline mr-2" />Your Referral Link
              </h2>
              <div className="flex gap-2">
                <div className="flex-1 bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm text-slate-700 font-mono truncate" data-testid="referral-link-display">
                  {referralLink}
                </div>
                <Button onClick={copyLink} className="shrink-0 bg-orange-500 hover:bg-orange-600 text-white" data-testid="copy-referral-link">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button onClick={shareLink} variant="outline" className="shrink-0" data-testid="share-referral-link">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Share this link with friends. When they sign up, you earn <strong>{stats.credits_per_referral} free download credits</strong>!
              </p>
            </div>

            {/* How it works */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8">
              <h2 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "Fraunces, serif" }}>How It Works</h2>
              <div className="flex flex-col sm:flex-row gap-6">
                {[
                  { step: "1", title: "Share Your Link", desc: "Copy your unique referral link and share it with friends" },
                  { step: "2", title: "Friend Signs Up", desc: "When they create an account using your link, we track the referral" },
                  { step: "3", title: "Earn Credits", desc: `You instantly receive ${stats.credits_per_referral} free download credits` },
                ].map((s) => (
                  <div key={s.step} className="flex-1 text-center">
                    <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 text-lg font-bold">
                      {s.step}
                    </div>
                    <p className="font-medium text-slate-900 text-sm">{s.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Referral history */}
            {stats.history?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="referral-history">
                <h2 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "Fraunces, serif" }}>Referral History</h2>
                <div className="divide-y divide-slate-100">
                  {stats.history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-sm text-slate-700">{h.referred_email}</p>
                        <p className="text-xs text-slate-400">{new Date(h.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className="text-sm font-medium text-green-600">+{h.credits_awarded} credits</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
